import { Section, CodeBlock, InfoBox } from "@/components"

export function NextShadcnServerClientPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Server & Client Components</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Understand when to use Server Components vs Client Components in Next.js 14. 
          Make the right choice for optimal performance and user experience.
        </p>
      </div>

      <Section title="Component Types" description="Server vs Client">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-xl font-semibold">Server Components</h3>
            <p className="text-sm text-muted-foreground">
              Default in App Router. Rendered on the server, never sent to client.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Fetch data directly</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Access backend resources</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Keep secrets safe</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Reduce bundle size</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-xl font-semibold">Client Components</h3>
            <p className="text-sm text-muted-foreground">
              Opt-in with "use client". Rendered on client, enable interactivity.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Use React hooks</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Handle events</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Use browser APIs</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Add interactivity</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <Section title="When to Use Each" description="Decision guide">
        <div className="space-y-6">
          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="font-semibold mb-4">Use Server Components for:</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2"><span className="text-primary">→</span><span>Fetching data from APIs or databases</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Accessing backend resources directly</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Keeping sensitive information server-side</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Reducing JavaScript sent to the client</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Static content and layouts</span></li>
            </ul>
          </div>
          
          <div className="bg-muted/30 rounded-lg p-6">
            <h3 className="font-semibold mb-4">Use Client Components for:</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2"><span className="text-primary">→</span><span>Adding interactivity (onClick, onChange, etc.)</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Using React hooks (useState, useEffect, etc.)</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Using browser APIs (localStorage, geolocation, etc.)</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Managing client-side state</span></li>
              <li className="flex gap-2"><span className="text-primary">→</span><span>Forms with real-time validation</span></li>
            </ul>
          </div>
        </div>
      </Section>

      <Section title="Examples" description="Common patterns">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Server Component (Data Fetching)</h3>
            <CodeBlock
              code={`// app/products/page.tsx
// Server Component (no "use client")

async function getProducts() {
  const res = await fetch('https://api.example.com/products', {
    cache: 'no-store' // Always fresh
  })
  return res.json()
}

export default async function ProductsPage() {
  const products = await getProducts()

  return (
    <div>
      <h1>Products</h1>
      <div className="grid gap-4">
        {products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Client Component (Interactivity)</h3>
            <CodeBlock
              code={`// components/AddToCartButton.tsx
"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"

interface Props {
  productId: string
  productName: string
}

export function AddToCartButton({ productId, productName }: Props) {
  const [isAdding, setIsAdding] = useState(false)
  const { toast } = useToast()

  const addToCart = async () => {
    setIsAdding(true)
    try {
      await fetch("/api/cart", {
        method: "POST",
        body: JSON.stringify({ productId })
      })
      toast({
        title: "Added to cart",
        description: \`\${productName} has been added to your cart\`
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add to cart",
        variant: "destructive"
      })
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <Button onClick={addToCart} disabled={isAdding}>
      {isAdding ? "Adding..." : "Add to Cart"}
    </Button>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Composing Server & Client</h3>
            <CodeBlock
              code={`// app/products/page.tsx (Server Component)
import { AddToCartButton } from "@/components/AddToCartButton"

async function getProducts() {
  // Server-side data fetching
  const res = await fetch('https://api.example.com/products')
  return res.json()
}

export default async function ProductsPage() {
  const products = await getProducts()

  return (
    <div className="grid gap-6">
      {products.map(product => (
        <div key={product.id} className="border rounded-lg p-6">
          <h2>{product.name}</h2>
          <p>{product.price}</p>
          {/* Client Component for interactivity */}
          <AddToCartButton 
            productId={product.id}
            productName={product.name}
          />
        </div>
      ))}
    </div>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Data Fetching Patterns" description="Server Components">
        <div className="space-y-6">
          <CodeBlock
            code={`// Sequential fetching
async function Page() {
  const user = await getUser()
  const posts = await getUserPosts(user.id) // waits for user
  return <div>...</div>
}

// Parallel fetching (recommended)
async function Page() {
  const userPromise = getUser()
  const postsPromise = getPosts()
  
  const [user, posts] = await Promise.all([userPromise, postsPromise])
  return <div>...</div>
}

// Streaming with Suspense
import { Suspense } from "react"

async function Page() {
  return (
    <div>
      <h1>Dashboard</h1>
      <Suspense fallback={<UserSkeleton />}>
        <UserData />
      </Suspense>
      <Suspense fallback={<PostsSkeleton />}>
        <PostsList />
      </Suspense>
    </div>
  )
}`}
            language="typescript"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Start with Server Components, only add "use client" when needed</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Move "use client" as deep as possible in the component tree</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Server Components can pass data to Client Components as props</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Suspense boundaries for better loading states</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Fetch data in parallel when possible for better performance</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

